import { lazy, type ComponentType } from "react";
import { MutationCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router";
import { Layout } from "@/components/layout/app-layout";
import { ProtectedRoute } from "@/components/auth/protected-route";
import { SettingsLayout } from "@/components/layout/settings-layout";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/lib/auth/auth-context";
import { env } from "@/lib/core/env";
import { WorkspaceProvider } from "@/lib/workspace/workspace-context";
import { LoginPage } from "@/routes/login";
import { NotFoundPage } from "@/routes/not-found";
import { planFeatureOf } from "@/lib/plans/plan-errors";
import { requestUpgrade } from "@/lib/plans/upgrade";

// Pages load on demand, so the first visit only downloads the shell and the page it opens.
const page = <K extends string>(load: () => Promise<Record<K, ComponentType>>, name: K) =>
  lazy(() => load().then((module) => ({ default: module[name] })));
const AssistantPage = page(() => import("@/routes/assistant"), "AssistantPage");
const ConfirmPasswordResetPage = page(
  () => import("@/routes/confirm-password-reset"),
  "ConfirmPasswordResetPage",
);
const ConfirmVerificationPage = page(
  () => import("@/routes/confirm-verification"),
  "ConfirmVerificationPage",
);
const OAuthRedirectPage = page(() => import("@/routes/oauth2-redirect"), "OAuthRedirectPage");
const CustomerDetailPage = page(() => import("@/routes/customer-detail"), "CustomerDetailPage");
const CustomersPage = page(() => import("@/routes/customers"), "CustomersPage");
const DashboardPage = page(() => import("@/routes/dashboard"), "DashboardPage");
const DevMailPage = page(() => import("@/routes/dev-mail"), "DevMailPage");
const InvoiceDetailPage = page(() => import("@/routes/invoice-detail"), "InvoiceDetailPage");
const CompanySettingsPage = page(() => import("@/routes/settings/company"), "CompanySettingsPage");
const InvoiceSettingsPage = page(() => import("@/routes/settings/invoice"), "InvoiceSettingsPage");
const InvitationsPage = page(() => import("@/routes/invitations"), "InvitationsPage");
const InvoicesPage = page(() => import("@/routes/invoices"), "InvoicesPage");
const RecurringPage = page(() => import("@/routes/recurring"), "RecurringPage");
const PrivacySettingsPage = page(() => import("@/routes/settings/privacy"), "PrivacySettingsPage");
const QuotesPage = page(() => import("@/routes/quotes"), "QuotesPage");
const QuoteDetailPage = page(() => import("@/routes/quote-detail"), "QuoteDetailPage");
const PlansPage = page(() => import("@/routes/settings/plans"), "PlansPage");
const AppsSettingsPage = page(() => import("@/routes/settings/apps"), "AppsSettingsPage");
const OAuthAuthorizePage = page(() => import("@/routes/oauth-authorize"), "OAuthAuthorizePage");
const PaymentSettingsPage = page(() => import("@/routes/settings/payment"), "PaymentSettingsPage");
const SettingsPage = page(() => import("@/routes/settings/profile"), "SettingsPage");
const TemplateDetailPage = page(() => import("@/routes/template-detail"), "TemplateDetailPage");
const TemplatesPage = page(() => import("@/routes/templates"), "TemplatesPage");
const WorkspaceSettingsPage = page(
  () => import("@/routes/settings/workspace"),
  "WorkspaceSettingsPage",
);

const queryClient = new QueryClient({
  // A write the server refuses because of the plan opens the upgrade dialog, wherever it came from.
  mutationCache: new MutationCache({
    onError: (error) => {
      const feature = planFeatureOf(error);
      if (feature) requestUpgrade(feature);
    },
  }),
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false },
  },
});

/** Plans live in the settings; old links and Stripe's return URLs keep their query. */
function PlansRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/settings/plans${search}`} replace />;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <WorkspaceProvider>
          <BrowserRouter>
            <Routes>
              <Route element={<Layout />}>
                <Route path="login" element={<LoginPage />} />
                {/* The links in account emails (server/pb_hooks/lib/mail.js sendAuth). */}
                <Route path="auth/confirm-verification" element={<ConfirmVerificationPage />} />
                <Route path="auth/confirm-password-reset" element={<ConfirmPasswordResetPage />} />
                {/* The end of the sign-in popup (deploy/Caddyfile redirects /_/ here). */}
                <Route path="auth/oauth2-redirect" element={<OAuthRedirectPage />} />
                <Route element={<ProtectedRoute />}>
                  <Route index element={<Navigate to="/dashboard" replace />} />
                  <Route path="dashboard" element={<DashboardPage />} />
                  <Route path="invoices" element={<InvoicesPage />} />
                  <Route path="invoices/recurring" element={<RecurringPage />} />
                  <Route path="invoices/:id" element={<InvoiceDetailPage />} />
                  <Route path="quotes" element={<QuotesPage />} />
                  <Route path="quotes/:id" element={<QuoteDetailPage />} />
                  <Route path="templates" element={<TemplatesPage />} />
                  <Route path="templates/:id" element={<TemplateDetailPage />} />
                  <Route path="customers" element={<CustomersPage />} />
                  <Route path="customers/:id" element={<CustomerDetailPage />} />
                  <Route path="assistant/:conversationId?" element={<AssistantPage />} />
                  <Route path="plans" element={<PlansRedirect />} />
                  <Route path="invitations" element={<InvitationsPage />} />
                  {/* Claude or ChatGPT asks to connect (services/mcp, OAuth consent). */}
                  <Route path="oauth/authorize" element={<OAuthAuthorizePage />} />
                  <Route path="settings" element={<SettingsLayout />}>
                    <Route index element={<SettingsPage />} />
                    <Route path="company" element={<CompanySettingsPage />} />
                    <Route path="invoice" element={<InvoiceSettingsPage />} />
                    <Route path="payment" element={<PaymentSettingsPage />} />
                    <Route path="workspace" element={<WorkspaceSettingsPage />} />
                    <Route path="plans" element={<PlansPage />} />
                    <Route path="apps" element={<AppsSettingsPage />} />
                    <Route path="privacy" element={<PrivacySettingsPage />} />
                  </Route>
                </Route>
                <Route path="*" element={<NotFoundPage />} />
              </Route>
              {env.isDev && <Route path="dev/mail" element={<DevMailPage />} />}
            </Routes>
          </BrowserRouter>
        </WorkspaceProvider>
        <Toaster
          richColors
          position="top-right"
          mobileOffset={{ top: "calc(16px + env(safe-area-inset-top))" }}
        />
      </AuthProvider>
    </QueryClientProvider>
  );
}
